import { useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Copy, Ellipsis, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Segmented,
  SettingsList,
  SettingsSection,
} from "@/components/settings/section";
import { ListSkeleton, QueryBoundary } from "@/components/shell/page";
import { useWorkspace } from "@/components/shell/workspace-context";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Tip } from "@/components/ui/tooltip";
import { api, type WorkspaceRole } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";

export const Route = createFileRoute("/_app/$workspaceSlug/settings/members")({
  component: MembersSettings,
});

function MembersSettings() {
  const { t } = useTranslation("settings");
  const { workspace } = useWorkspace();
  const canManage = workspace.role !== "member";
  return (
    <>
      {canManage ? <InviteForm /> : null}
      <SettingsSection title={t("members.title")}>
        <QueryBoundary>
          <MemberList />
        </QueryBoundary>
      </SettingsSection>
      {canManage ? (
        <QueryBoundary>
          <Invitations />
        </QueryBoundary>
      ) : null}
    </>
  );
}

function InviteForm() {
  const { t } = useTranslation("settings");
  const { t: tErrors } = useTranslation("errors");
  const { workspace } = useWorkspace();
  const config = useQuery(api.users.authConfig);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    setError(null);
    setPending(true);
    const result = await authClient.organization.inviteMember({
      organizationId: workspace.organizationId,
      email: address,
      role,
    });
    setPending(false);
    if (result.error) {
      setError(authErrorMessage(result.error, tErrors));
      return;
    }
    setEmail("");
    toast(
      config?.emailDelivery
        ? t("members.invited", { email: address })
        : t("members.invitedNoEmail", { email: address }),
    );
  }

  return (
    <SettingsSection title={t("members.invite")} description={t("members.inviteHint")}>
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t("members.email")} htmlFor="invite-email" className="min-w-52 flex-1">
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Segmented
            label={t("members.role")}
            value={role}
            onChange={setRole}
            options={[
              { value: "member", label: t("roles.member") },
              { value: "admin", label: t("roles.admin") },
            ]}
          />
          <Button type="submit" disabled={pending}>
            {pending ? <Spinner /> : null}
            {t("members.send")}
          </Button>
        </div>
        <FormError>{error}</FormError>
      </form>
    </SettingsSection>
  );
}

type Member = {
  memberId: string;
  userId: string;
  name: string;
  email: string;
  image: string | null;
  role: WorkspaceRole;
};

function MemberList() {
  const { workspace } = useWorkspace();
  const members = useQuery(api.workspaces.members, { workspaceId: workspace._id });
  if (members === undefined) return <ListSkeleton rows={3} className="rounded-lg border" />;
  return (
    <SettingsList>
      {members.map((member) => (
        <MemberRow key={member.memberId} member={member} />
      ))}
    </SettingsList>
  );
}

function MemberRow({ member }: { member: Member }) {
  const { t } = useTranslation("settings");
  const { t: tErrors } = useTranslation("errors");
  const { workspace, me } = useWorkspace();
  const navigate = useNavigate();
  // Shown the moment a role is picked; the list catches up when the change
  // arrives, and an error puts the real value back.
  const [pendingRole, setPendingRole] = useState<WorkspaceRole | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);

  const isSelf = member.userId === me.id;
  const role = pendingRole ?? member.role;
  // Admins manage members and admins; only an owner touches an owner.
  const canManage =
    workspace.role === "owner" || (workspace.role === "admin" && member.role !== "owner");
  const grantable: WorkspaceRole[] =
    workspace.role === "owner" ? ["member", "admin", "owner"] : ["member", "admin"];

  async function changeRole(next: WorkspaceRole) {
    if (next === member.role) return;
    setPendingRole(next);
    const result = await authClient.organization.updateMemberRole({
      organizationId: workspace.organizationId,
      memberId: member.memberId,
      role: next,
    });
    if (result.error) toast.error(authErrorMessage(result.error, tErrors));
    setPendingRole(null);
  }

  async function remove() {
    setRemoving(true);
    const result = isSelf
      ? await authClient.organization.leave({ organizationId: workspace.organizationId })
      : await authClient.organization.removeMember({
          organizationId: workspace.organizationId,
          memberIdOrEmail: member.memberId,
        });
    setRemoving(false);
    if (result.error) {
      toast.error(authErrorMessage(result.error, tErrors));
      setConfirming(false);
      return;
    }
    setConfirming(false);
    if (isSelf) void navigate({ to: "/" });
  }

  return (
    <li className="flex min-h-11 items-center gap-3 px-3 py-1.5">
      <Avatar name={member.name} image={member.image} size="md" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">
          {member.name}
          {isSelf ? <span className="font-normal text-tertiary"> · {t("members.you")}</span> : null}
        </span>
        <span className="type-label truncate text-muted-foreground">{member.email}</span>
      </div>
      <span className="shrink-0 text-muted-foreground">{t(`roles.${role}`)}</span>
      {canManage || isSelf ? (
        <DropdownMenu>
          <Tip label={t("members.actions", { name: member.name })}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("members.actions", { name: member.name })}
              >
                <Ellipsis aria-hidden strokeWidth={1.75} />
              </Button>
            </DropdownMenuTrigger>
          </Tip>
          <DropdownMenuContent align="end">
            {canManage && !isSelf ? (
              <>
                <DropdownMenuLabel>{t("members.role")}</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={role}
                  onValueChange={(value) => void changeRole(value as WorkspaceRole)}
                >
                  {grantable.map((option) => (
                    <DropdownMenuRadioItem key={option} value={option}>
                      {t(`roles.${option}`)}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuItem destructive onSelect={() => setConfirming(true)}>
              {isSelf ? t("members.leave") : t("members.remove")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={
          isSelf
            ? t("members.leaveTitle", { workspace: workspace.name })
            : t("members.removeTitle", { name: member.name })
        }
        description={isSelf ? t("members.leaveBody") : t("members.removeBody")}
        confirmLabel={isSelf ? t("members.leave") : t("members.remove")}
        destructive
        pending={removing}
        onConfirm={() => void remove()}
      />
    </li>
  );
}

function Invitations() {
  const { t, i18n } = useTranslation("settings");
  const { t: tErrors } = useTranslation("errors");
  const { workspace } = useWorkspace();
  const invitations = useQuery(api.workspaces.pendingInvitations, {
    workspaceId: workspace._id,
  });
  const [now] = useState(() => Date.now());
  if (!invitations || invitations.length === 0) return null;

  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });

  async function copy(id: string) {
    await navigator.clipboard.writeText(`${window.location.origin}/invite/${id}`);
    toast(t("members.linkCopied"));
  }

  async function revoke(id: string) {
    const result = await authClient.organization.cancelInvitation({ invitationId: id });
    if (result.error) toast.error(authErrorMessage(result.error, tErrors));
  }

  return (
    <SettingsSection title={t("members.pending")}>
      <SettingsList>
        {invitations.map((invitation) => (
          <li key={invitation.id} className="flex min-h-11 items-center gap-3 px-3 py-1.5">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-medium">{invitation.email}</span>
              <span className="type-label truncate text-muted-foreground">
                {invitation.expiresAt < now
                  ? t("members.expired")
                  : t("members.expires", { date: date.format(invitation.expiresAt) })}
              </span>
            </div>
            <span className="shrink-0 text-muted-foreground">{t(`roles.${invitation.role}`)}</span>
            <Tip label={t("members.copyLink")}>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("members.copyLink")}
                onClick={() => void copy(invitation.id)}
              >
                <Copy aria-hidden strokeWidth={1.75} />
              </Button>
            </Tip>
            <Tip label={t("members.revoke")}>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("members.revoke")}
                onClick={() => void revoke(invitation.id)}
              >
                <X aria-hidden strokeWidth={1.75} />
              </Button>
            </Tip>
          </li>
        ))}
      </SettingsList>
    </SettingsSection>
  );
}
